// Full Story Video's list — Full Story's, for packages made from footage.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiFilm, FiTrash2, FiUsers, FiUser, FiSearch, FiCheckCircle, FiLayers } from 'react-icons/fi';
import { fullStoryVideoService as fullStoryService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { formatSeconds, showError } from '../FullStory/shared';

const TEAM_ROLES = ['admin', 'superadmin', 'manager'];

/** Full Story home: start a story, and the history of stories (own, or the team's for managers/admins). */
export const FullStoryVideoList = () => {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canSeeTeam = TEAM_ROLES.includes(user?.role);

  const [scope, setScope] = useState('mine');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      setData(await fullStoryService.list({ scope, userName: scope === 'team' ? search || undefined : undefined, page }));
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  }, [scope, search]);

  useEffect(() => {
    const timer = setTimeout(() => load(1), scope === 'team' ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, scope]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const story = await fullStoryService.create({ title: t('fullStoryVideo.untitled') });
      navigate(`/full-story-video/${story._id}`);
    } catch (e) {
      showError(e);
      setCreating(false);
    }
  };

  const handleDelete = async (e, story) => {
    e.stopPropagation();
    if (!window.confirm(t('fullStory.deleteConfirm', { title: story.title }))) return;
    try {
      await fullStoryService.remove(story._id);
      setData((d) => ({ ...d, items: d.items.filter((s) => s._id !== story._id), total: d.total - 1 }));
    } catch (err) {
      showError(err);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0">
            <FiFilm size={20} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('fullStoryVideo.title')}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{t('fullStoryVideo.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate('/full-story-video/templates/plates')} className="btn btn-secondary flex items-center gap-2">
            <FiLayers size={15} />
            {t('fullStoryVideo.plateTemplates')}
          </button>
          <button onClick={handleCreate} disabled={creating} className="btn btn-primary flex items-center gap-2">
            <FiPlus size={15} />
            {creating ? t('fullStory.creating') : t('fullStoryVideo.newStory')}
          </button>
        </div>
      </div>

      {/* How it works */}
      <ol className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
        {['script', 'voice', 'images', 'video'].map((step, i) => (
          <li key={step} className="rounded-xl border border-gray-200 bg-white px-3 py-2.5">
            <span className="text-xs font-semibold text-primary-600">{i + 1}. {t(`fullStoryVideo.step.${step}`)}</span>
            <p className="text-xs text-gray-500 mt-0.5">{t(`fullStoryVideo.stepHint.${step}`)}</p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-3">
        {canSeeTeam && (
          <div role="tablist" className="flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
            {[['mine', FiUser, t('fullStory.myStories')], ['team', FiUsers, t('fullStory.teamStories')]].map(([id, Icon, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={scope === id}
                onClick={() => setScope(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${scope === id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>
        )}
        {scope === 'team' && (
          <label className="relative flex-1 min-w-[200px] max-w-sm">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('fullStory.searchByUser')}
              className="input w-full pl-9 text-sm"
            />
          </label>
        )}
        <span className="text-sm text-gray-500 ml-auto">{t('fullStory.count', { count: data.total })}</span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('fullStory.loading')}</p>
      ) : data.items.length === 0 ? (
        <div className="card text-center py-12">
          <FiFilm size={28} className="mx-auto text-gray-300" />
          <p className="mt-3 text-sm text-gray-600">{scope === 'team' ? t('fullStory.noTeamStories') : t('fullStoryVideo.noStories')}</p>
          {scope === 'mine' && (
            <button onClick={handleCreate} className="btn btn-primary mt-4 inline-flex items-center gap-2">
              <FiPlus size={14} /> {t('fullStoryVideo.newStory')}
            </button>
          )}
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.items.map((story) => (
            <li key={story._id}>
              <button
                onClick={() => navigate(`/full-story-video/${story._id}`)}
                className="w-full text-left rounded-2xl border border-gray-200 bg-white overflow-hidden hover:border-gray-300 hover:shadow-sm transition"
              >
                <div className="relative aspect-video bg-gray-100">
                  {story.thumbnail ? (
                    <img src={fullStoryService.fileUrl(story._id, story.thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-300"><FiFilm size={28} /></div>
                  )}
                  {story.hasVideo && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[11px] font-medium px-2 py-0.5">
                      <FiCheckCircle size={11} /> {t('fullStory.videoReady')}
                    </span>
                  )}
                  <span className="absolute top-2 right-2 rounded-full bg-black/60 text-white text-[11px] font-medium px-2 py-0.5">{story.aspectRatio}</span>
                </div>
                <div className="p-3.5">
                  <div className="flex items-start gap-2">
                    <p className="flex-1 min-w-0 font-semibold text-gray-900 truncate">{story.title}</p>
                    {scope === 'mine' && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleDelete(e, story)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(e, story); }}
                        title={t('fullStory.delete')}
                        className="flex-shrink-0 p-1 -m-1 rounded text-gray-400 hover:text-danger-600 hover:bg-danger-50"
                      >
                        <FiTrash2 size={14} />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t(`fullStoryVideo.step.${story.step}`)} · {story.durationSec ? formatSeconds(story.durationSec) : t('fullStory.targetLength', { seconds: story.targetSeconds })}
                    {' · '}{new Date(story.updatedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                  </p>
                  {scope === 'team' && story.owner && <p className="text-xs text-gray-400 mt-0.5 truncate">{story.owner.name}</p>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <button disabled={data.page <= 1} onClick={() => load(data.page - 1)} className="btn btn-ghost text-sm disabled:opacity-40">{t('fullStory.prev')}</button>
          <span className="text-sm text-gray-500 self-center">{data.page} / {data.pages}</span>
          <button disabled={data.page >= data.pages} onClick={() => load(data.page + 1)} className="btn btn-ghost text-sm disabled:opacity-40">{t('fullStory.next')}</button>
        </div>
      )}
    </div>
  );
};

export default FullStoryVideoList;
